import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { cn } from '../lib/utils';
import { hoyLocal, toYMD } from '../utils/fechaLocal';

interface CouponForm {
  code: string;
  name: string;
  description: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_amount: number;
  max_discount: number;
  applies_to: 'all' | 'products' | 'services' | 'specific_product' | 'specific_brand' | 'specific_category';
  target_id: string;
  target_name: string;
  start_date: string;
  end_date: string;
  max_uses: number;
  max_uses_per_client: number;
}

const DatePicker = ({ value, onChange, id }: { value?: string; onChange: (date: string) => void; id?: string }) => {
  const [open, setOpen] = useState(false);
  const dateValue = value ? new Date(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          id={id}
          className={cn(
            "w-full justify-start text-left font-normal",
            !dateValue && "text-muted-foreground"
          )}
        >
          {dateValue ? dateValue.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Seleccionar fecha'}
          <ChevronDownIcon className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          captionLayout="dropdown"
          fromYear={1900}
          toYear={new Date().getFullYear() + 10}
          onSelect={(date: Date | undefined) => {
            onChange(date ? toYMD(date) : '');
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

const CreateCoupon: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm<CouponForm>({
    defaultValues: {
      code: '',
      name: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 0,
      min_amount: 0,
      max_discount: 0,
      applies_to: 'all',
      target_id: '',
      target_name: '',
      start_date: hoyLocal(),
      end_date: toYMD(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)),
      max_uses: 100,
      max_uses_per_client: 1
    }
  });

  useEffect(() => {
    if (isEditing) fetchCoupon();
  }, []);

  const fetchCoupon = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/coupons/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const c = response.data;
      setValue('code', c.code);
      setValue('name', c.name);
      setValue('description', c.description);
      setValue('discount_type', c.discount_type);
      setValue('discount_value', c.discount_value);
      setValue('min_amount', c.min_amount);
      setValue('max_discount', c.max_discount || 0);
      setValue('applies_to', c.applies_to);
      setValue('target_id', c.target_id || '');
      setValue('target_name', c.target_name || '');
      setValue('start_date', c.start_date);
      setValue('end_date', c.end_date);
      setValue('max_uses', c.max_uses || 100);
      setValue('max_uses_per_client', c.max_uses_per_client || 1);
    } catch (error) {
      console.error('Error cargando cupón:', error);
      toast.error('Error cargando cupón');
      navigate('/discounts');
    }
  };

  const onSubmit = async (data: CouponForm) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (isEditing) {
        await axios.put(`/api/coupons/${id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cupón actualizado exitosamente');
      } else {
        await axios.post('/api/coupons', data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Cupón creado exitosamente');
      }
      navigate('/discounts');
    } catch (error: any) {
      console.error('Error guardando cupón:', error);
      toast.error(isEditing ? 'Error actualizando cupón' : 'Error creando cupón: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/discounts')} className="size-8">
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          {isEditing ? 'Editar Cupón' : 'Nuevo Cupón'}
        </h1>
      </div>

      <Card>
        <CardContent className="p-4 sm:p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Código */}
            <div className="space-y-2">
              <Label htmlFor="coupon-code" className="font-medium text-sm">Código del Cupón *</Label>
              <Input
                id="coupon-code"
                {...register('code', {
                  required: 'El código es requerido',
                  pattern: {
                    value: /^[A-Z0-9_-]+$/,
                    message: 'Solo letras mayúsculas, números, guiones y guiones bajos'
                  }
                })}
                className="px-4 py-3 rounded-xl border border-input bg-background uppercase"
                placeholder="DESCUENTO20"
              />
              {errors.code && <p className="text-sm text-red-600 mt-1">{errors.code.message}</p>}
            </div>

            {/* Nombre */}
            <div className="space-y-2">
              <Label htmlFor="coupon-name" className="font-medium text-sm">Nombre del Cupón *</Label>
              <Input
                id="coupon-name"
                {...register('name', { required: 'El nombre es requerido' })}
                className="px-4 py-3 rounded-xl border border-input bg-background"
                placeholder="Descuento de Bienvenida"
              />
              {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>}
            </div>

            {/* Descripción */}
            <div className="space-y-2">
              <Label htmlFor="coupon-description" className="font-medium text-sm">Descripción</Label>
              <Textarea
                id="coupon-description"
                {...register('description')}
                rows={3}
                className="px-4 py-3 rounded-xl border border-input bg-background resize-none"
                placeholder="Descripción del cupón..."
              />
            </div>

            {/* Tipo y valor */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-medium text-sm">Tipo de Descuento *</Label>
                <Select onValueChange={(v) => setValue('discount_type', v as 'percentage' | 'fixed')} value={watch('discount_type')}>
                  <SelectTrigger className="h-10 rounded-xl"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                    <SelectItem value="fixed">Monto Fijo ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="coupon-value" className="font-medium text-sm">Valor del Descuento *</Label>
                <Input
                  id="coupon-value"
                  type="number"
                  step="0.01"
                  {...register('discount_value', {
                    required: 'El valor es requerido',
                    min: { value: 0.01, message: 'El valor debe ser mayor a 0' }
                  })}
                  className="px-4 py-3 rounded-xl border border-input bg-background"
                  placeholder="20"
                />
                {errors.discount_value && <p className="text-sm text-red-600 mt-1">{errors.discount_value.message}</p>}
              </div>
            </div>

            {/* Monto mínimo y descuento máximo */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-medium text-sm">Monto Mínimo ($)</Label>
                <Input type="number" step="0.01" {...register('min_amount')} className="px-4 py-3 rounded-xl border border-input bg-background" placeholder="0" />
              </div>
              <div className="space-y-2">
                <Label className="font-medium text-sm">Descuento Máximo ($)</Label>
                <Input type="number" step="0.01" {...register('max_discount')} className="px-4 py-3 rounded-xl border border-input bg-background" placeholder="Sin límite" />
              </div>
            </div>

            {/* Aplica a */}
            <div className="space-y-2">
              <Label className="font-medium text-sm">Aplica a *</Label>
              <Select onValueChange={(v) => setValue('applies_to', v as any)} value={watch('applies_to')}>
                <SelectTrigger className="h-10 rounded-xl"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los productos y servicios</SelectItem>
                  <SelectItem value="products">Solo productos</SelectItem>
                  <SelectItem value="services">Solo servicios</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Fechas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-medium text-sm">Fecha de Inicio</Label>
                <DatePicker value={watch('start_date')} onChange={(d) => setValue('start_date', d)} />
              </div>
              <div className="space-y-2">
                <Label className="font-medium text-sm">Fecha de Expiración</Label>
                <DatePicker value={watch('end_date')} onChange={(d) => setValue('end_date', d)} />
              </div>
            </div>

            {/* Límites de uso */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-medium text-sm">Máximo Total de Usos</Label>
                <Input type="number" {...register('max_uses')} className="px-4 py-3 rounded-xl border border-input bg-background" placeholder="100" />
              </div>
              <div className="space-y-2">
                <Label className="font-medium text-sm">Usos por Cliente</Label>
                <Input type="number" {...register('max_uses_per_client')} className="px-4 py-3 rounded-xl border border-input bg-background" placeholder="1" />
              </div>
            </div>

            {/* Botones */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate('/discounts')} className="h-10 rounded-lg px-6">
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="h-10 rounded-lg px-6 bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90"
              >
                {loading ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear Cupón'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateCoupon;
